
import React from 'react';
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import FilterSearch from './FilterSearch';
import FilterLocation from './FilterLocation';
import FilterDate from './FilterDate';
import FilterTypes from './FilterTypes';
import FilterCreditPoints from './FilterCreditPoints';
import FilterProfessions from './FilterProfessions';
import ActiveFilters from './ActiveFilters';
import { EventType } from '@/lib/types';

interface MobileFilterSheetProps {
  search: string;
  setSearch: (search: string) => void;
  location: string;
  setLocation: (location: string) => void;
  selectedTypes: Record<EventType, boolean>;
  selectedProfessions: Record<string, boolean>;
  hasCreditPoints: boolean;
  dateRange: { startDate: string; endDate: string };
  currentDate?: Date;
  handleTypeClick: (type: EventType) => void;
  handleProfessionClick: (id: string) => void;
  toggleCreditPointsFilter: () => void;
  resetFilters: () => void;
  setDateRange: (dateRange: { startDate: string; endDate: string }) => void;
  hasActiveFilters: boolean;
}

const MobileFilterSheet: React.FC<MobileFilterSheetProps> = ({
  search,
  setSearch,
  location,
  setLocation,
  selectedTypes,
  selectedProfessions,
  hasCreditPoints,
  dateRange,
  currentDate,
  handleTypeClick,
  handleProfessionClick,
  toggleCreditPointsFilter,
  resetFilters,
  setDateRange,
  hasActiveFilters
}) => {
  // Convert Record<EventType, boolean> to EventType[] for the components that expect arrays
  const selectedTypesArray = Object.entries(selectedTypes)
    .filter(([_, selected]) => selected)
    .map(([type]) => type as EventType);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="md:hidden">
          Szűrők
          {hasActiveFilters && (
            <span className="ml-1 text-xs bg-medihub-primary text-white rounded-full w-5 h-5 inline-flex items-center justify-center">
              {Object.values(selectedTypes).filter(Boolean).length + 
               Object.values(selectedProfessions).filter(Boolean).length +
               (location ? 1 : 0) + 
               (hasCreditPoints ? 1 : 0) + 
               ((dateRange.startDate || dateRange.endDate) ? 1 : 0)}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-full sm:w-[350px] overflow-y-auto">
        <div className="h-full flex flex-col">
          <div className="flex justify-between items-center pb-4 border-b">
            <h3 className="font-medium text-lg">Szűrők</h3>
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              Alaphelyzet
            </Button>
          </div>
          
          <div className="space-y-6 py-4 flex-1 overflow-y-auto">
            <FilterSearch search={search} onSearchChange={setSearch} />
            <FilterLocation location={location} onLocationChange={setLocation} />
            <FilterDate 
              dateRange={dateRange} 
              onDateChange={(newDateRange) => setDateRange(newDateRange)} 
              defaultMonth={currentDate} 
            />
            <FilterCreditPoints hasCreditPoints={hasCreditPoints} onToggle={toggleCreditPointsFilter} />
            <FilterProfessions 
              selectedProfessions={selectedProfessions} 
              onProfessionClick={handleProfessionClick} 
            />
            <FilterTypes 
              selectedTypes={selectedTypesArray} 
              onTypeClick={handleTypeClick} 
            />
          </div>
          
          {hasActiveFilters && (
            <div className="pt-4 border-t">
              <ActiveFilters 
                search={search} 
                location={location} 
                selectedTypes={selectedTypesArray} 
                hasCreditPoints={hasCreditPoints} 
                dateRange={dateRange} 
                onTypeClick={handleTypeClick} 
                onLocationClear={() => setLocation('')} 
                onCreditPointsClear={() => toggleCreditPointsFilter()}
                onDateRangeClear={key => {
                  const newDateRange = { ...dateRange };
                  newDateRange[key] = '';
                  setDateRange(newDateRange);
                }} 
                onResetAll={resetFilters} 
              />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileFilterSheet;
